import plpStyles from "src/organisms/ProductList/ProductList.module.scss";
import { PageHead } from "src/molecules/PageHead";
import type {
  ProductListingViewProps,
  ProductListType,
} from "src/types/product-listing";
import { HeaderContainer } from "src/organisms/ProductList/ProductList.styled";
import { useLingui } from "@lingui/react";
import { msg, t } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import { getDeepLink } from "src/utils/getDeepLink";
import { buildPlpPageTitle } from "src/utils/buildPlpPageTitle";
import { ProductList } from "src/organisms/ProductList/ProductList";
import { PopularBrandsWrapper } from "../PopularBrands";
import {
  localeFixOptional,
  ShopType,
} from "src/utils/genderLocale/genderLocale";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { Breadcrumb } from "src/organisms/Breadcrumb";
import getAllBrandsPageRoute from "src/utils/getAllBrandsPageRoute";
import type { Locale } from "src/lib/Intl";
import { BrandBanner } from "./components/BrandBanner";
import { CollectionsHeader } from "./components/CollectionsHeader";
import Cookies from "js-cookie";
import { OT_SHOP_TYPE_COOKIE, PRODUCT_LIST_TYPE } from "src/utils/constants";
import { useUserPreferences } from "src/hooks/stores/use-app-store";
import { storeConfig } from "src/config/app-config";
import { usePLPSchema } from "src/hooks/usePLPSchema";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";
import type { ListingFacetKeys } from "src/types/adapters/listing.type";

const HIDE_CATEGORY_TREE: ProductListType[] = [
  PRODUCT_LIST_TYPE.COLLECTIONS_PLP,
];

const HIDE_FILTERS_PER_PLP_TYPE: Partial<
  Record<ProductListType, ListingFacetKeys[]>
> = {
  [PRODUCT_LIST_TYPE.BRAND_PLP]: ["brands"],
};

const BREADCRUMB_TITLE_TRANSLATIONS: Record<string, MessageDescriptor> = {
  brands: msg`Brands`,
  "all products": msg`All products`,
};

const getHighestDiscount = (
  products: { price: number; regular_price: number }[] = []
): number =>
  Math.max(
    0,
    ...products.map(({ price, regular_price }) =>
      regular_price > 0 ? Math.round((1 - price / regular_price) * 100) : 0
    )
  );

function ProductListingView({
  title,
  description,
  gender,
  slug,
  defaultSorting,
  locale,
  productListType,
  breadcrumbs,
  limit,
  navigationItems,
  initialListingState,
  ...props
}: ProductListingViewProps & { query?: string }): React.ReactElement {
  const { i18n } = useLingui();
  const userPreferences = useUserPreferences();
  const router = useRouter();

  // The search query is part of the URL and the <title>, so results pages stay
  // out of the index no matter what a user types (STORE-1342).
  const isSearchPlp = productListType === "SEARCH_PLP";

  const resolvedShopType = useMemo(() => {
    const shopTypeFromUrl = localeFixOptional({ gender });
    if (shopTypeFromUrl) return shopTypeFromUrl;

    const preferredShopType = localeFixOptional({
      gender: userPreferences?.preferred_shop_type,
    });
    if (preferredShopType) return preferredShopType;

    const cookieShopType = localeFixOptional({
      gender: Cookies.get(OT_SHOP_TYPE_COOKIE),
    });
    if (cookieShopType) return cookieShopType;

    return ShopType.Women;
  }, [gender, userPreferences?.preferred_shop_type]);

  // Generate CollectionPage + Corporation schemas for this PLP
  usePLPSchema({
    productListType,
    title: title || "",
    description,
    gender,
    slug: slug || "",
    brand: "brand" in props ? props.brand : undefined,
  });

  const excludeFilters = useMemo<ListingFacetKeys[]>(() => {
    const hiddenFilters = HIDE_FILTERS_PER_PLP_TYPE[productListType] ?? [];

    return gender && gender !== ShopType.All
      ? [...hiddenFilters, "genders"]
      : [...hiddenFilters];
  }, [gender, productListType]);

  const viewAllLink = useMemo(
    () =>
      getAllBrandsPageRoute({
        shopType: resolvedShopType,
        locale: locale as Locale,
      }),
    [resolvedShopType, locale]
  );

  const deepLink = useMemo(() => {
    if (productListType === PRODUCT_LIST_TYPE.SEARCH_PLP) {
      return getDeepLink({
        route: ["search"],
        query: {
          q: props.query,
        },
      });
    }

    if (productListType === PRODUCT_LIST_TYPE.BRAND_PLP) {
      return getDeepLink({
        route: ["brands", localeFixOptional({ gender }), slug],
        query: {
          cat_slug: router.query?.cat_slug,
          discount: router.query?.discount,
        },
      });
    }

    return getDeepLink({
      route: ["product-category", localeFixOptional({ gender }), slug],
      query: {
        cat_slug: router.query?.cat_slug,
        discount: router.query?.discount,
      },
    });
  }, [productListType, gender, slug, router.query, props]);

  const localizedBreadCrumb = useMemo(
    () =>
      breadcrumbs?.map((item) => {
        const translation = item.shopType
          ? shopTypeTranslation[item.shopType]
          : BREADCRUMB_TITLE_TRANSLATIONS[item.title.toLowerCase()];
        return translation ? { ...item, title: i18n._(translation) } : item;
      }) || [],
    [breadcrumbs, i18n]
  );

  const highestDiscount = getHighestDiscount(initialListingState?.products);
  const countryCode = storeConfig?.countryCode ?? "";
  let pageDescription = description;
  if (productListType === PRODUCT_LIST_TYPE.BRAND_PLP && title) {
    pageDescription =
      highestDiscount > 0
        ? t(
            i18n
          )`Shop ${title} with discounts up to ${highestDiscount}% at Otrium ${countryCode}`
        : t(i18n)`Shop ${title} at Otrium ${countryCode}`;
  }

  return (
    <>
      <PageHead
        title={buildPlpPageTitle({
          i18n,
          productListType,
          title:
            title ||
            ("collection" in props ? props.collection.title : undefined),
          gender,
        })}
        description={pageDescription}
        deeplink={deepLink}
        noindex={isSearchPlp}
        enableIndexation={!isSearchPlp}
        skipSeoContent
        seoContent={props.seoContent}
        hreflangCategorySlugs={props.hreflangCategorySlugs}
      />

      <div className={plpStyles.contentContainer}>
        <HeaderContainer>
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <Breadcrumb
              // Brand crumbs carry full hrefs; the gender prefix would 404 the genderless brand link.
              gender={
                productListType === PRODUCT_LIST_TYPE.BRAND_PLP
                  ? undefined
                  : gender
              }
              data={localizedBreadCrumb}
            />
          ) : null}

          {"brand" in props ? (
            <BrandBanner brand={props.brand} genderId={resolvedShopType} />
          ) : null}

          {"collection" in props ? (
            <CollectionsHeader collection={props.collection} />
          ) : null}
        </HeaderContainer>

        <ProductList
          title={title || undefined}
          slug={slug || ""}
          categoryId={
            "categoryId" in props ? props.categoryId ?? undefined : undefined
          }
          gender={gender}
          pageContentType={productListType}
          initialListingState={initialListingState || {}}
          excludeFilters={excludeFilters}
          navigationItems={navigationItems}
          hideCategoryTree={HIDE_CATEGORY_TREE.includes(productListType)}
          collectionId={"collection" in props ? props.collection.id : undefined}
          query={"query" in props ? (props.query as string) : undefined}
          brandSameAs={"brandSameAs" in props ? props.brandSameAs : undefined}
        />

        <PopularBrandsWrapper
          genderId={resolvedShopType}
          localeGenderId={resolvedShopType}
          excludeSlug={slug || undefined}
          viewAllLink={viewAllLink}
          productListType={productListType}
        />
      </div>
    </>
  );
}

export { ProductListingView };
